// src/components/dfs/PlayerPool.tsx
// UI-ONLY REDESIGN: sticky headers (desktop), right-hand Salary + Add (mobile & desktop)
// No data/logic changes. Same props. Same virtualization/infinite scroll.

import React, { memo, useEffect, FC, useRef, RefObject, useState, ReactNode } from 'react';
import { Lock, CircleCheck } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ApiPlayer, ApiCompetitionSlim } from '@/api/dfs';
import { formatPlayerDisplayName } from '@/utils/formatting';
import { formatMatchupAbbreviation, leagueSizes } from '@/utils/teams';
import { getOprkColorClass, formatMatchTime, formatMatchDateTime, OPRK_TOOLTIP } from '@/utils/playerDisplay';
import { useNow } from '@/hooks/useNow';
import PlayerProfileModal from '@/components/common/PlayerProfileModal';
import { fetchAllCompetitions } from '@/api/competitions';
import HorizontalPillFilter from '@/components/common/HorizontalPillFilter';
import Spinner from '@/components/common/Spinner';
import CoachMark from '@/components/common/CoachMark';
import { useCoachMark } from '@/hooks/useCoachMark';
import AvailabilityBadge from '@/components/common/AvailabilityBadge';
import InjuryBadge from '@/components/common/InjuryBadge';

const logger = console;

export type SortableKeys =
  | 'primary_position'
  | 'nickname'
  | 'current_team_name'
  | 'dfs_salary'
  | 'fppg'
  | 'oprk';

export type PositionFilter = 'ALL' | 'FW' | 'MF' | 'DF' | 'GK';

interface PlayerPoolProps {
  players: ApiPlayer[];
  onAddPlayer: (player: ApiPlayer) => void;
  lineupPlayerIds?: Set<number>;
  lockedPlayerIds: Set<number>;

  positionFilter: PositionFilter;
  onPositionFilterChange: (filter: PositionFilter) => void;

  searchTerm: string;
  onSearchTermChange: (term: string) => void;

  sortConfig: { key: SortableKeys | null; direction: 'asc' | 'desc' };
  onSortConfigChange: (config: { key: SortableKeys | null; direction: 'asc' | 'desc' }) => void;

  hideFilters?: boolean;
  hideSearch?: boolean;
  playerFetchError: string | null;
  onRetry?: () => void;
  remainingSalary?: number;
  onPlayerClick?: (playerId: number) => void;
  leagueShortNameForOprkContext?: string | null;
  externalScrollContainerRef?: RefObject<HTMLDivElement | null>;

  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  hasMore?: boolean; // parent passes !!hasNextPage
  isSearching?: boolean; // true while a search/filter query is in-flight
  dfsContestId?: number | null;
  rankCompetitionIds?: number[];
  /** When true (the mobile add-player tray), shows a one-time "Tap a player to
   *  research" coach mark on the first row. */
  enableResearchCoachMark?: boolean;
}

const PlayerPool: FC<PlayerPoolProps> = (props) => {
  const {
    players,
    onAddPlayer,
    lineupPlayerIds = new Set(),
    lockedPlayerIds,

    positionFilter,
    onPositionFilterChange,

    searchTerm,
    onSearchTermChange,

    sortConfig,
    onSortConfigChange,

    hideFilters = false,
    hideSearch = false,
    playerFetchError,
    onRetry,
    remainingSalary,
    onPlayerClick,
    leagueShortNameForOprkContext,

    externalScrollContainerRef,

    onLoadMore,
    isLoadingMore,
    hasMore,
    dfsContestId,
    rankCompetitionIds,
    enableResearchCoachMark = false,
  } = props;

  // First-run "Tap a player to research" coach mark, anchored to the first
  // player row. Shows once per device while a tray is open with players loaded.
  const firstRowRef = useRef<HTMLButtonElement>(null);
  const { show: showResearchCoach, dismiss: dismissResearchCoach } = useCoachMark(
    'tray_research',
    enableResearchCoachMark && players.length > 0
  );

  const now = useNow();

  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [selectedPlayerForModal, setSelectedPlayerForModal] = useState<ApiPlayer | null>(null);
  const [modalCompetitionInfo, setModalCompetitionInfo] = useState<{ id: number; name: string } | null>(null);
  const [allCompetitions, setAllCompetitions] = useState<ApiCompetitionSlim[]>([]);

  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const desktopScrollRef = useRef<HTMLDivElement>(null);

  const getActiveScrollEl = (): HTMLDivElement | null => {
    // If a parent provided a container, prefer it
    if (externalScrollContainerRef?.current) return externalScrollContainerRef.current;

    // Match your Tailwind breakpoint (sm = 640px)
    const isMobile =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(max-width: 639px)').matches;

    return isMobile ? mobileScrollRef.current : desktopScrollRef.current;
  };

  // Sentinel for IntersectionObserver
  const observerRef = useRef<HTMLDivElement>(null);

  const filterItems = [
    { id: 'ALL' as const, name: 'All' },
    { id: 'FW' as const, name: 'FW' },
    { id: 'MF' as const, name: 'MF' },
    { id: 'DF' as const, name: 'DF' },
    { id: 'GK' as const, name: 'GK' },
  ];

  // Reset scroll position when filters/search/sort change (server handles the filtering)
  useEffect(() => {
    const el = getActiveScrollEl();
    if (el) el.scrollTop = 0;
  }, [positionFilter, searchTerm, sortConfig]);

  // Load competitions for PlayerProfileModal context
  useEffect(() => {
    const loadCompetitions = async () => {
      try {
        const comps = await fetchAllCompetitions(true);
        setAllCompetitions(comps);
      } catch (error) {
        logger.error('PlayerPool: Failed to load competitions for modal context', error);
      }
    };
    loadCompetitions();
  }, []);

  // Infinite scrolling
  useEffect(() => {
    const rootEl = getActiveScrollEl();
    const target = observerRef.current;

    if (!rootEl || !target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore && !isLoadingMore && onLoadMore) {
          onLoadMore();
        }
      },
      {
        // IMPORTANT: use the active scroll container
        root: rootEl,
        // Trigger before we hit the absolute bottom so we can preload
        rootMargin: '0px 0px 200px 0px',
        // Be lenient; don't require 100% visibility
        threshold: 0,
      }
    );

    observer.observe(target);

    // If content isn't tall enough to scroll, keep fetching
    const checkAndLoadMore = () => {
      if (
        rootEl.scrollHeight <= rootEl.clientHeight &&
        hasMore &&
        !isLoadingMore &&
        onLoadMore
      ) {
        // eslint-disable-next-line no-console
        console.log(
          '[PlayerPool Infinite Scroll] Content shorter than container; fetching next page.'
        );
        onLoadMore();
      }
    };

    // Check shortly after render
    const t1 = setTimeout(checkAndLoadMore, 100);
    // Also re-check a bit later in case virtual rows resize
    const t2 = setTimeout(checkAndLoadMore, 300);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      observer.disconnect();
    };
    // Re-run when list size or paging state changes
  }, [players.length, hasMore, isLoadingMore, onLoadMore]);

  const handlePlayerRowClickForProfile = (player: ApiPlayer) => {
    if (onPlayerClick) {
      onPlayerClick(player.id);
      return;
    }
    setSelectedPlayerForModal(player);
    const leagueKey = leagueShortNameForOprkContext?.toUpperCase();
    const targetComp = leagueKey
      ? allCompetitions.find((c) => c.short_name.toUpperCase() === leagueKey)
      : undefined;
    if (targetComp) setModalCompetitionInfo({ id: targetComp.id, name: targetComp.name });
    else if (allCompetitions.length > 0) setModalCompetitionInfo({ id: allCompetitions[0].id, name: allCompetitions[0].name });
    else setModalCompetitionInfo(null);
    setIsProfileModalOpen(true);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    onSearchTermChange(event.target.value);

  const sortOptions: { value: string; label: string }[] = [
    { value: 'dfs_salary-desc', label: 'Salary (High-Low)' },
    { value: 'fppg-desc', label: 'FPPG (High-Low)' },
    { value: 'oprk-asc', label: 'OPRK (Low-High)' },
    { value: 'nickname-asc', label: 'Name (A-Z)' },
    { value: 'dfs_salary-asc', label: 'Salary (Low-High)' },
    { value: 'fppg-asc', label: 'FPPG (Low-High)' },
    { value: 'oprk-desc', label: 'OPRK (High-Low)' },
    { value: 'nickname-desc', label: 'Name (Z-A)' },
  ];

  const handleSortSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const [key, direction] = event.target.value.split('-') as [SortableKeys, 'asc' | 'desc'];
    onSortConfigChange({ key, direction });
  };

  // Virtualizer over the server-provided `players` list
  const rowVirtualizer = useVirtualizer({
    // keep your bottom extra row when hasMore
    count: hasMore ? players.length + 1 : players.length,
    // point at the ACTUAL scroll element (mobile or desktop)
    getScrollElement: () => getActiveScrollEl(),
    estimateSize: () => {
      const isMobile = typeof window !== 'undefined' && window.matchMedia?.('(max-width: 639px)').matches;
      return isMobile ? 56 : 40;
    },
    overscan: 6,
  });

  // Keep a ref to latest players so infinite-scroll page-loads don't re-trigger the snap
  const playersRef = useRef(players);
  useEffect(() => { playersRef.current = players; }, [players]);

  // Auto-snap to first affordable player when remaining salary changes (salary-desc sort only)
  useEffect(() => {
    if (sortConfig.key !== 'dfs_salary' || sortConfig.direction !== 'desc') return;
    if (remainingSalary === undefined) return;

    const targetIdx = playersRef.current.findIndex(
      (p) => (p.dfs_salary ?? 0) <= remainingSalary
    );
    if (targetIdx <= 0) return; // All affordable (already at top) or none found — no snap needed

    const id = requestAnimationFrame(() => {
      rowVirtualizer.scrollToIndex(targetIdx, { align: 'start' });
    });
    return () => cancelAnimationFrame(id);
  }, [remainingSalary, sortConfig]); // rowVirtualizer is a stable TanStack Virtual instance

  // Derive per-row UI state — 4 mutually exclusive states evaluated in priority order:
  // 1. Locked (match started) → lock icon
  // 2. In Lineup → gray checkmark
  // 3. Can't Add (unaffordable / no salary) → gray "+ Add"
  // 4. Can Add → green "+ Add"
  const uiFor = (player: ApiPlayer) => {
    const isLocked = lockedPlayerIds.has(player.id);
    const isPlayerInLineup = lineupPlayerIds.has(player.id);
    const isSalaryValid = typeof player.dfs_salary === 'number' && player.dfs_salary >= 0;
    const isAffordable =
      remainingSalary === undefined || !isSalaryValid || player.dfs_salary! <= remainingSalary;

    // Determine state
    let buttonText: ReactNode;
    let buttonTitle: string;
    let isDisabled: boolean;
    let rowShadeClass: string;
    let rowDimClass: string;

    if (isLocked) {
      buttonText = <Lock size={14} />;
      buttonTitle = 'Match Started';
      isDisabled = true;
      rowShadeClass = 'hover:bg-primary/60 active:bg-primary/60';
      rowDimClass = 'opacity-60';
    } else if (isPlayerInLineup) {
      buttonText = <CircleCheck size={14} />;
      buttonTitle = 'In Lineup';
      isDisabled = true;
      rowShadeClass = 'outline outline-1 -outline-offset-1 outline-green-500 rounded';
      rowDimClass = '';
    } else if (!isAffordable || !isSalaryValid) {
      buttonText = '+ Add';
      buttonTitle = 'Add Player';
      isDisabled = true;
      rowShadeClass = 'hover:bg-primary/60 active:bg-primary/60';
      rowDimClass = 'opacity-60';
    } else {
      buttonText = '+ Add';
      buttonTitle = 'Add Player';
      isDisabled = false;
      rowShadeClass = 'hover:bg-primary/60 active:bg-primary/60';
      rowDimClass = '';
    }

    const canAdd = !isDisabled;

    const salaryText = isSalaryValid ? `$${player.dfs_salary!.toLocaleString('en-US')}` : '-';
    const salaryColor = isSalaryValid && !isAffordable ? 'text-secondary' : 'text-accent-yellow';

    const displayName = formatPlayerDisplayName(player);
    const matchupText = formatMatchupAbbreviation(player.current_team_name, player.opponent);
    const canOpenProfile = !isLocked;

    const leagueSize =
      leagueShortNameForOprkContext && leagueSizes[leagueShortNameForOprkContext]
        ? leagueSizes[leagueShortNameForOprkContext]
        : 20;
    const oprkColorClass = getOprkColorClass(player.oprk, leagueSize);

    const fppgDisplay = player.fppg?.toFixed(1) ?? '0.0';
    const oprkDisplay = player.oprk?.toFixed(0) ?? '-';

    const matchDateTime = formatMatchDateTime(player.match_time, now);
    // When locked, show match status (FT, LIVE) instead of kickoff time
    const timeDisplay = isLocked && player.match_status_code
      ? player.match_status_code
      : (matchDateTime ?? formatMatchTime(player.match_time, false));
    const timeColorClass = isLocked
      ? (player.match_status_code === 'LIVE' ? 'text-green-400' : 'text-gray-500')
      : (matchDateTime ? 'text-white' : 'text-gray-500');

    const addBtnClasses = `px-3 py-1 rounded text-xs font-bold transition-colors
      ${isDisabled ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-500 active:bg-green-500 text-white'}`;

    return {
      isLocked,
      isDisabled,
      canAdd,
      buttonText,
      buttonTitle,
      salaryText,
      salaryColor,
      displayName,
      matchupText,
      canOpenProfile,
      oprkColorClass,
      fppgDisplay,
      oprkDisplay,
      timeDisplay,
      timeColorClass,
      rowShadeClass,
      rowDimClass,
      addBtnClasses,
    };
  };

  // ---------- RENDER ----------
  return (
    <>
      <div className="bg-primary p-4 lg:p-2 rounded-lg shadow-lg flex flex-col text-gray-200 font-body flex-grow min-h-0">
        {/* Search */}
        {!hideSearch && (
          <div className="mb-2 relative">
            <input
              type="text"
              placeholder="Search Players..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full p-3 rounded-lg bg-surface-input text-white border-2 border-gray-700 focus:border-accent-yellow focus:outline-none placeholder-gray-500 text-base font-medium"
            />
          </div>
        )}

        {showResearchCoach && (
          <CoachMark
            targetRef={firstRowRef}
            text="Tap a player to research."
            onDismiss={dismissResearchCoach}
          />
        )}

        {/* Mobile sort */}
        <div className="sm:hidden mb-3 flex items-center space-x-2">
          <label
            htmlFor="sort-select-mobile-pool"
            className="text-xs font-semibold uppercase text-accent-yellow flex-shrink-0"
          >
            SORT BY
          </label>
          <select
            id="sort-select-mobile-pool"
            value={sortConfig.key ? `${sortConfig.key}-${sortConfig.direction}` : 'dfs_salary-desc'}
            onChange={handleSortSelectChange}
            className="flex-grow p-2 rounded-lg bg-accent-yellow text-primary font-bold border-2 border-accent-border focus:outline-none text-sm cursor-pointer shadow-md"
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Filters */}
        {!hideFilters && (
          <div className="mb-3">
            <HorizontalPillFilter
              items={filterItems}
              selectedId={positionFilter}
              onSelect={(id) => onPositionFilterChange(id as PositionFilter)}
            />
          </div>
        )}

        {/* ---------- MOBILE LIST (default) ---------- */}
        <div
          className="sm:hidden overflow-y-auto flex-grow border-t border-gray-700"
          ref={mobileScrollRef}
        >
          {/* Optional mini header to label the right-side cluster */}
          <div className="sticky top-0 z-10 bg-primary/95 backdrop-blur border-b border-gray-700">
                <div className="px-2 py-2 text-caption uppercase tracking-wide text-accent-light-blue font-heading font-bold flex justify-between">
                <span>Player · Pos · Opp</span>
                <span>Salary · Add</span>
                </div>
          </div>

          <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
            {rowVirtualizer.getVirtualItems().map((v) => {
            const player = players[v.index];
            if (!player) {
              // This is the "phantom" extra row when hasMore = true
              return (
                <div
                  key={v.key}
                  className="absolute top-0 left-0 w-full flex items-center justify-center"
                  style={{ height: `${v.size}px`, transform: `translateY(${v.start}px)` }}
                >
                  {hasMore ? <Spinner size="sm" color="text-accent-yellow" /> : null}
                </div>
              );
            }

            const ui = uiFor(player);

            return (
                <div
                key={v.key}
                className="absolute top-0 left-0 w-full"
                style={{ height: `${v.size}px`, transform: `translateY(${v.start}px)` }}
                >
                <div className={`flex items-stretch ${ui.rowShadeClass} ${ui.rowDimClass} border-b border-gray-800 px-2 py-2`}>
                    {/* Left: Player + secondary rows */}
                    <button
                    ref={v.index === 0 ? firstRowRef : undefined}
                    type="button"
                    className="flex-1 min-w-0 text-left pr-3"
                    onClick={() => (ui.canOpenProfile ? handlePlayerRowClickForProfile(player) : undefined)}
                    title={ui.displayName}
                    >
                    <div className="min-w-0">
                        <span className={`block min-w-0 truncate font-medium text-gray-100 ${ui.canOpenProfile ? 'active:text-accent-yellow' : ''}`}>{ui.displayName}</span>
                    </div>

                    <div className="mt-0.5 flex min-w-0 items-center gap-1 overflow-hidden text-caption text-gray-400">
                        <span className="flex-shrink-0 rounded bg-gray-800 px-1.5 py-0.5 text-xs text-gray-300">
                        {player.primary_position || 'N/A'}
                        </span>
                        <AvailabilityBadge status={player.availability_status} isLocked={ui.isLocked} />
                        <InjuryBadge status={player.injury_status} details={player.injury_details} />
                        <span className="ml-1 min-w-0 truncate">
                            FPPG <span className="text-accent-yellow">{ui.fppgDisplay}</span>
                            <span className="mx-1">·</span>
                            <span className={ui.oprkColorClass} title={OPRK_TOOLTIP}>OPRK {ui.oprkDisplay}</span>
                        </span>
                    </div>
                    </button>

                    {/* Right: Salary + Add (thumb-friendly) */}
                    <div className="flex flex-col items-end justify-center min-w-[112px]">
                    <span className={`font-heading font-bold ${ui.salaryColor}`}>{ui.salaryText}</span>
                    <button
                        onClick={(e) => { e.stopPropagation(); if (ui.canAdd) onAddPlayer(player); }}
                        disabled={ui.isDisabled}
                        className={ui.addBtnClasses + ' mt-1'}
                        title={ui.buttonTitle}
                        aria-label={ui.buttonTitle}
                    >
                        {ui.buttonText}
                    </button>
                    </div>
                </div>
                </div>
            );
            })}
          </div>

          {/* Infinite scroll sentinel */}
          <div ref={observerRef} className="h-10 flex justify-center items-center mt-2">
                {isLoadingMore && <Spinner size="sm" color="text-accent-yellow" />}
                {!hasMore && players.length > 0 && !isLoadingMore && (
                <p className="text-xs text-gray-500 italic">All players loaded</p>
                )}
          </div>
        </div>

        {/* ---------- DESKTOP TABLE (>= sm) ---------- */}
        <div
          className="hidden sm:block overflow-y-auto flex-grow border-t border-gray-700"
          ref={desktopScrollRef}
        >
          {/* Sticky header */}
          <div className="sticky top-0 z-10 bg-primary/95 backdrop-blur">
            <div
              className="grid items-center px-3 py-1.5 border-b border-gray-700
                         text-caption uppercase tracking-wide text-accent-light-blue font-heading font-bold
                         grid-cols-[minmax(0,2fr)_42px_minmax(0,1.2fr)_56px_50px_82px_72px]"
            >
              {/* Sortable: Name */}
              <button
                type="button"
                className="flex items-center gap-1 text-left hover:text-white active:text-white transition-colors"
                onClick={() => onSortConfigChange({ key: 'nickname', direction: sortConfig.key === 'nickname' && sortConfig.direction === 'desc' ? 'asc' : 'desc' })}
              >
                Name
                <span className="text-badge opacity-50">
                  {sortConfig.key === 'nickname' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                </span>
              </button>

              {/* Sortable: Pos */}
              <button
                type="button"
                className="flex items-center justify-center gap-0.5 hover:text-white active:text-white transition-colors"
                onClick={() => onSortConfigChange({ key: 'primary_position', direction: sortConfig.key === 'primary_position' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })}
              >
                Pos
                <span className="text-badge opacity-50">
                  {sortConfig.key === 'primary_position' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                </span>
              </button>

              {/* Non-sortable: Opp */}
              <div>Opp / Time</div>

              {/* Sortable: FPPG */}
              <button
                type="button"
                className="flex items-center justify-end gap-1 hover:text-white active:text-white transition-colors w-full"
                onClick={() => onSortConfigChange({ key: 'fppg', direction: sortConfig.key === 'fppg' && sortConfig.direction === 'desc' ? 'asc' : 'desc' })}
              >
                <span className="text-badge opacity-50">
                  {sortConfig.key === 'fppg' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                </span>
                FPPG
              </button>

              {/* Sortable: OPRK */}
              <button
                type="button"
                className="flex items-center justify-end gap-1 hover:text-white active:text-white transition-colors w-full"
                onClick={() => onSortConfigChange({ key: 'oprk', direction: sortConfig.key === 'oprk' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })}
                title={OPRK_TOOLTIP}
              >
                <span className="text-badge opacity-50">
                  {sortConfig.key === 'oprk' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                </span>
                OPRK
              </button>

              {/* Sortable: Salary */}
              <button
                type="button"
                className="flex items-center justify-end gap-1 hover:text-white active:text-white transition-colors w-full"
                onClick={() => onSortConfigChange({ key: 'dfs_salary', direction: sortConfig.key === 'dfs_salary' && sortConfig.direction === 'desc' ? 'asc' : 'desc' })}
              >
                <span className="text-badge opacity-50">
                  {sortConfig.key === 'dfs_salary' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                </span>
                Salary
              </button>

              <div className="text-center">Add</div>
            </div>
          </div>

          {/* Rows */}
          <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
            {rowVirtualizer.getVirtualItems().map((v) => {
              const player = players[v.index];
              if (!player) {
                return (
                  <div
                    key={v.key}
                    className="absolute top-0 left-0 w-full flex items-center justify-center"
                    style={{ height: `${v.size}px`, transform: `translateY(${v.start}px)` }}
                  >
                    {hasMore ? <Spinner size="sm" color="text-accent-yellow" /> : null}
                  </div>
                );
              }

              const ui = uiFor(player);

              return (
                <div
                  key={v.key}
                  className="absolute top-0 left-0 w-full"
                  style={{ height: `${v.size}px`, transform: `translateY(${v.start}px)` }}
                >
                  <div
                    className={`grid items-center px-3 h-full ${ui.rowShadeClass} ${ui.rowDimClass} border-b border-gray-800
                                grid-cols-[minmax(0,2fr)_42px_minmax(0,1.2fr)_56px_50px_82px_72px]`}
                  >
                    {/* Name (clickable for profile) */}
                    <button
                      type="button"
                      className={`flex items-center gap-1.5 text-left pr-3 min-w-0 ${ui.canOpenProfile ? 'group/name' : ''}`}
                      onClick={() => (ui.canOpenProfile ? handlePlayerRowClickForProfile(player) : undefined)}
                      title={ui.displayName}
                    >
                      <span className={`font-medium text-gray-100 truncate ${ui.canOpenProfile ? 'group-hover/name:text-accent-yellow group-hover/name:underline group-hover/name:underline-offset-2' : ''}`}>{ui.displayName}</span>
                      <AvailabilityBadge status={player.availability_status} isLocked={ui.isLocked} />
                      <InjuryBadge status={player.injury_status} details={player.injury_details} />
                    </button>

                    {/* Pos */}
                    <div className="text-center">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-gray-800 text-gray-300">
                        {player.primary_position || 'N/A'}
                      </span>
                    </div>

                    {/* Opp / Time */}
                    <div className="text-xs text-gray-400 truncate">
                      <span>{ui.matchupText}</span>
                      {player.match_time && (
                        <span className={ui.timeColorClass}>
                          {` · ${ui.timeDisplay}`}
                        </span>
                      )}
                    </div>

                    {/* FPPG */}
                    <div className="text-right">
                      <span className="font-semibold text-accent-yellow">{ui.fppgDisplay}</span>
                    </div>

                    {/* OPRK */}
                    <div className={`text-right font-semibold ${ui.oprkColorClass}`}>{ui.oprkDisplay}</div>

                    {/* Salary */}
                    <div className={`text-right font-heading font-bold ${ui.salaryColor}`}>{ui.salaryText}</div>

                    {/* Add */}
                    <div className="text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); if (ui.canAdd) onAddPlayer(player); }}
                        disabled={ui.isDisabled}
                        className={ui.addBtnClasses}
                        title={ui.buttonTitle}
                        aria-label={ui.buttonTitle}
                      >
                        {ui.buttonText}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Infinite scroll sentinel */}
          <div ref={observerRef} className="h-10 flex justify-center items-center mt-2">
            {isLoadingMore && <Spinner size="md" color="text-accent-yellow" />}
            {!hasMore && players.length > 0 && !isLoadingMore && (
              <p className="text-sm text-gray-500 italic">All players loaded</p>
            )}
          </div>
        </div>

        {/* Empty state */}
        {players.length === 0 && (
          playerFetchError ? (
            <div role="alert" className="flex flex-col items-center gap-3 p-4 text-center text-sm text-gray-300">
              <p>{playerFetchError}</p>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="inline-flex min-h-10 items-center justify-center rounded-lg border border-gray-600 px-4 font-semibold text-gray-100 transition-colors duration-150 hover:border-accent-yellow hover:text-accent-yellow active:bg-accent-yellow/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow"
                >
                  Try again
                </button>
              )}
            </div>
          ) : (
            <div className="p-4 text-center text-sm italic text-gray-500">
              {searchTerm.trim()
                ? 'No players match - the pool covers active EPL and La Liga players only.'
                : 'No players match your filters.'}
            </div>
          )
        )}
      </div>

      {/* Player Profile Modal */}
      {selectedPlayerForModal && modalCompetitionInfo && (
        <PlayerProfileModal
          isOpen={isProfileModalOpen}
          onClose={() => {
            setIsProfileModalOpen(false);
            setSelectedPlayerForModal(null);
            setModalCompetitionInfo(null);
          }}
          player={selectedPlayerForModal}
          competitionId={modalCompetitionInfo.id}
          rankCompetitionIds={rankCompetitionIds}
          competitionName={modalCompetitionInfo.name}
          dfsContestId={dfsContestId}
        />
      )}
    </>
  );
};

export default memo(PlayerPool);
